// src/App.js
import React, { useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import HomeScreen from './components/HomeScreen';
import MenuScreen from './components/MenuScreen';
import DocumentListScreen from './components/DocumentListScreen';
import DocumentViewer from './components/DocumentViewer';
import SettingsScreen from './components/SettingsScreen';
import SyncProgressDialog from './components/SyncProgressDialog';
import UpdateDetectionDialog from './components/UpdateDetectionDialog';
import SyncStatusIndicator from './components/SyncStatusIndicator';
import { storage } from './firebase';
import DocumentService from './services/DocumentService';
import AutoCacheService from './services/AutoCacheService';
import { ThemeProvider } from './contexts/ThemeContext';
import BandwidthLimitedDownloadService from './services/BandwidthLimitedDownloadService';

// Reintroduce the VersionCheckService for silent updates
import VersionCheckService from './services/VersionCheckService';

import { initializeCompatibilityLayer } from './services/CompatibilityLayer';

function App() {
  // Application state
  const [currentScreen, setCurrentScreen] = useState('home');
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDocumentName, setSelectedDocumentName] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [showOnlineAlert, setShowOnlineAlert] = useState(false);
  
  // Auto-caching state
  const [showSyncProgress, setShowSyncProgress] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false); // For document update detection dialog
  const [documentUpdates, setDocumentUpdates] = useState([]);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [showStatusIndicator, setShowStatusIndicator] = useState(false);
  
  // New state for caching prompt
  const [showCachingPrompt, setShowCachingPrompt] = useState(false);
  
  // New state for silent update notification message
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Check for document updates and silent updates when coming back online
  useEffect(() => {
    const checkForDocumentUpdates = async () => {
      if (!navigator.onLine) return;
      
      try {
        setCheckingUpdates(true);
        const result = await AutoCacheService.checkForUpdates();
        
        if (result.status === 'complete' && result.updates && result.updates.length > 0) {
          // Check user preference for auto-update
          const autoUpdate = localStorage.getItem('autoUpdate');
          if (autoUpdate === 'true') {
            // Apply updates automatically
            AutoCacheService.applyUpdates(result.updates);
          } else {
            // Show the update dialog for document updates
            setDocumentUpdates(result.updates);
            setShowUpdateDialog(true);
          }
        }
      } catch (error) {
        console.error("Error checking for updates:", error);
      } finally {
        setCheckingUpdates(false);
      }
    };

    // Silent update check function as per instructions
    const checkForSilentUpdate = async () => {
      try {
        const result = await VersionCheckService.checkForUpdates();
        if (result.updateNeeded) {
          // Silently perform the update
          await VersionCheckService.performUpdate();
          
          // Optionally show a non-intrusive notification
          setSnackbarMessage('App updated to the latest version');
          setShowOnlineAlert(true);
        }
      } catch (error) {
        console.error('Error during silent update check:', error);
      }
    };

    const handleOnline = () => {
      setIsOffline(false);
      // Check for a silent update upon regaining connectivity
      checkForSilentUpdate();
      setShowOnlineAlert(true);
      
      // When coming back online, check for document updates if auto-sync is enabled
      const autoSync = localStorage.getItem('autoSync');
      if (autoSync !== 'false') {
        checkForDocumentUpdates();
        DocumentService.syncWithServer();
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
      setShowOfflineAlert(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also perform a silent update check on initial load if online
    if (navigator.onLine) {
      setTimeout(checkForSilentUpdate, 2000);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize services and keyboard shortcuts
  useEffect(() => {
    // Initialize the compatibility layer to ensure all required functions exist
    initializeCompatibilityLayer();
    
    if (storage) {
      console.log("Firebase Storage initialized successfully!");
    } else {
      console.error("Failed to initialize Firebase Storage.");
    }

    // Initialize the BandwidthLimitedDownloadService
    BandwidthLimitedDownloadService.initialize();
    
    // Keyboard shortcuts for developer tools
    const handleKeyDown = (event) => {
      // Force sync shortcut (Ctrl+Shift+S)
      if (event.ctrlKey && event.shiftKey && event.key === 'S') {
        setShowSyncProgress(true);
        AutoCacheService.initialize();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Apply document updates
  const handleApplyUpdates = async () => {
    setShowUpdateDialog(false);
    await AutoCacheService.applyUpdates(documentUpdates);
  };
  
  // Handle automatic caching choice
  const handleAutoCachingChoice = (choice) => {
    setShowCachingPrompt(false);
    
    if (choice) {
      // User wants automatic caching
      localStorage.setItem('autoCaching', 'true');
      // Start background caching
      BandwidthLimitedDownloadService.startBackgroundCaching();
    } else {
      // User doesn't want automatic caching
      localStorage.setItem('autoCaching', 'false');
    }
    
    // Navigate to menu
    setCurrentScreen('menu');
  };
  
  // Navigation handlers
  const handleOpenMenu = () => {
    // Check if we need to show the caching prompt
    const hasSetCachingPreference = localStorage.getItem('autoCaching');
    
    if (!hasSetCachingPreference) {
      // Show the caching prompt
      setShowCachingPrompt(true);
    } else {
      // Direct to menu
      setCurrentScreen('menu');
    }
  };

  // Navigation handler that accepts both section name and path
  const handleNavigateToSection = (sectionName, sectionPath, specialScreen = null) => {
    console.log(`Navigating to: ${sectionName}, Path: ${sectionPath}, Special: ${specialScreen}`);
    
    if (specialScreen) {
      setCurrentScreen(specialScreen);
      return;
    }
    
    if (sectionName === 'Home') {
      setCurrentScreen('home');
      return;
    }
    
    if (sectionName === 'Settings') {
      setCurrentScreen('settings');
      return;
    }
    
    setSelectedSection({ name: sectionName, path: sectionPath });
    setCurrentScreen('documentList');
  };

  const handleGoBackToMenu = () => {
    setCurrentScreen('menu');
    setSelectedSection(null);
  };

  const handleSelectDocument = (documentId, documentName) => {
    setSelectedDocumentId(documentId);
    setSelectedDocumentName(documentName);
    setCurrentScreen('documentViewer');
    
    if (documentId) {
      DocumentService.cacheDocumentForOffline(documentId)
        .then(() => console.log(`Document ${documentName} cached for offline use`))
        .catch(err => console.error(`Error caching document: ${err.message}`));
    }
  };

  const handleGoBackToDocumentList = () => {
    setCurrentScreen('documentList');
    setSelectedDocumentId(null);
    setSelectedDocumentName(null);
  };

  const handleNavigateToScreen = (screenName) => {
    // If screenName is 'debug', redirect to settings instead
    if (screenName === 'debug') {
      setCurrentScreen('settings');
    } else {
      setCurrentScreen(screenName);
    }
  };
  
  const handleManualSync = () => {
    setShowSyncProgress(true);
    AutoCacheService.initialize();
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'home':
        return <HomeScreen onOpen={handleOpenMenu} />;
      case 'menu':
        return <MenuScreen onNavigate={handleNavigateToSection} />;
      case 'documentList':
        return (
          <DocumentListScreen
            section={selectedSection}
            onBack={handleGoBackToMenu}
            onSelectDocument={handleSelectDocument}
            onManualSync={handleManualSync}
          />
        );
      case 'documentViewer':
        return (
          <DocumentViewer
            documentId={selectedDocumentId}
            documentName={selectedDocumentName}
            onBack={handleGoBackToDocumentList}
          />
        );
      case 'settings':
        return (
          <SettingsScreen 
            onBack={handleGoBackToMenu}
            onTriggerSync={handleManualSync}
          />
        );
      default:
        return <HomeScreen onOpen={handleOpenMenu} />;
    }
  };

  return (
    <ThemeProvider>
      <CssBaseline />
      {renderScreen()}

      {/* Offline Alert */}
      <Snackbar
        open={showOfflineAlert}
        autoHideDuration={3000}
        onClose={() => setShowOfflineAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowOfflineAlert(false)} severity="warning" sx={{ width: '100%' }}>
          You are now offline. Using cached content.
        </Alert>
      </Snackbar>

      {/* Online Alert */}
      <Snackbar
        open={showOnlineAlert}
        autoHideDuration={3000}
        onClose={() => setShowOnlineAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowOnlineAlert(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage || "You are back online."}
        </Alert>
      </Snackbar>
      
      {/* Sync Progress Dialog - shown only when manually triggered */}
      <SyncProgressDialog 
        open={showSyncProgress}
        onClose={() => setShowSyncProgress(false)}
        autoClose={true}
      />
      
      {/* Update Detection Dialog for document updates */}
      <UpdateDetectionDialog
        open={showUpdateDialog}
        onClose={() => setShowUpdateDialog(false)}
        updates={documentUpdates}
        loading={checkingUpdates}
        onApplyUpdates={handleApplyUpdates}
      />
      
      {/* Caching Preference Dialog */}
      <Dialog
        open={showCachingPrompt}
        onClose={() => handleAutoCachingChoice(false)}
        aria-labelledby="caching-dialog-title"
        aria-describedby="caching-dialog-description"
      >
        <DialogTitle id="caching-dialog-title">Enable Offline Access?</DialogTitle>
        <DialogContent>
          <DialogContentText id="caching-dialog-description">
            Would you like the app to gradually download documents for offline use?
            Documents will be cached in the background using only 1MB of data per day.
            You can change this preference later in Settings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleAutoCachingChoice(false)}>No Thanks</Button>
          <Button onClick={() => handleAutoCachingChoice(true)} autoFocus>
            Yes, Enable
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;
